import React from 'react';
import { Link } from 'react-router-dom';

/**
 * BrandKitPage — landing page for the Elevated brand assets.
 * Route: /brand-kit (public). Works identically on preview and production
 * because it's a real React route, not a static HTML file.
 */

const swatches = [
  { name: 'Ink', hex: '#0F0B08' },
  { name: 'Bone', hex: '#F5EFE6' },
  { name: 'Sand', hex: '#D9CCB8' },
  { name: 'Saddle Gold', hex: '#C8A672' },
  { name: 'Warm Gold', hex: '#D9BD88' },
  { name: 'Ember', hex: '#FF6B4A' },
  { name: 'Success', hex: '#4A7A5B' },
  { name: 'Destructive', hex: '#C94A3A' },
  { name: 'Warning', hex: '#D1A144' },
];

const LOGO = 'https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png';

const Card = ({ label, name, meta, href, download, primary, external }) => (
  <a
    href={href}
    {...(download ? { download: true } : {})}
    {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    className={`block rounded-xl p-6 border transition-all hover:-translate-y-0.5 ${
      primary
        ? 'col-span-full border-[#c8a672] bg-gradient-to-br from-[#c8a672]/15 to-transparent'
        : 'border-[#c8a672]/25 hover:border-[#c8a672]'
    }`}
  >
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-2">
      {label}
    </div>
    <div className="font-display font-bold uppercase text-xl sm:text-2xl mb-1">
      {name} {primary && <span className="text-[#c8a672]">→</span>}
    </div>
    <div className="text-sm text-[#d9ccb8]">{meta}</div>
  </a>
);

const BrandKitPage = () => (
  <div className="min-h-screen bg-[#0f0b08] text-[#f5efe6] py-16 px-6" style={{ fontFamily: 'Inter, sans-serif' }}>
    <div className="max-w-3xl mx-auto">
      <img src={LOGO} alt="Elevated Roping Media" className="h-20 mb-10" />
      <p className="text-[11px] uppercase tracking-[0.4em] text-[#c8a672] mb-4 font-medium">
        Brand Kit · v1.0
      </p>
      <h1 className="font-display font-bold uppercase text-5xl sm:text-6xl leading-[0.95] tracking-tight mb-3">
        Every asset
        <br />
        <span className="italic font-light text-[#c8a672]">in one place.</span>
      </h1>
      <p className="text-[#d9ccb8] text-base sm:text-lg leading-relaxed mb-10 max-w-xl">
        Download the brand book, the logo, the color palette, and the tokens
        file. Everything's ready to drop into Figma, InDesign, sponsor decks,
        or a new hire's onboarding folder.
      </p>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="brand-kit-swatches">
        {swatches.map((s) => (
          <div
            key={s.hex}
            className="w-10 h-10 rounded-md border border-[#c8a672]/25"
            style={{ background: s.hex }}
            title={`${s.name} ${s.hex}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card
          primary
          label="Read · in browser"
          name="Elevated Brand Book"
          meta="Complete brand doc · logo, colors, typography, voice, copy templates"
          href="/brand-book"
        />
        <Card
          label="Download · PDF"
          name="Brand Book (PDF)"
          meta="9-page print-ready version"
          href="/brand-kit/Elevated-Brand-Book.pdf"
          external
        />
        <Card
          label="Download · PNG"
          name="Elevated Logo"
          meta="Master file · transparent background"
          href={LOGO}
          external
        />
        <Card
          label="Download · JSON"
          name="Color Tokens"
          meta="Machine-readable palette for Figma / dev"
          href="/brand-kit/colors.json"
          external
        />
        <Card
          label="Download · ZIP"
          name="Everything (Zip)"
          meta="Whole kit in one archive"
          href="/brand-kit/elevated-brand-kit.zip"
          external
        />
      </div>

      <p className="mt-16 font-mono text-[10px] uppercase tracking-[0.4em] text-[#c8a672]/60 text-center">
        Elevated Roping Media · Internal · Feb 2026
      </p>
    </div>
  </div>
);

export default BrandKitPage;
